/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/pages/Auth.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { LogIn, KeyRound, ShieldCheck, UserPlus, LogOut, RotateCw, Mail, ArrowLeft } from 'lucide-react';
import { I, Spinner } from '../components/ui';
import { usePath, navigate } from '../lib/router';
import { setAuth, useAuth, type AuthState } from '../lib/users';

/**
 * Sign-in (ADR-030, ADR-033): the gate in front of the app. It asks the server what is needed — the first admin
 * (setup), a code by e-mail or a password, the second factor, or TOTP enrolment when an admin made it compulsory —
 * and shows the app only then. Invitation links (/invite#…) are accepted with a first code by e-mail or by choosing a
 * password; reset links (/reset#…) set a password. The public demo passes straight through.
 */

async function post<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error ?? `Fehler ${r.status}`);
  return data as T;
}

export function AuthGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const path = usePath().split('?')[0];
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/auth/state');
      if (!r.ok) throw new Error(`Server antwortet mit ${r.status}`);
      setAuth(await r.json() as AuthState);
      setError(null);
    } catch (e) { setError((e as Error).message === 'Failed to fetch' ? 'Keine Verbindung zum Server' : (e as Error).message); }
  }, []);
  useEffect(() => {
    load();
    const h = () => { load(); };
    window.addEventListener('kontor-auth', h);
    return () => window.removeEventListener('kontor-auth', h);
  }, [load]);

  if (!auth) {
    return (
      <AuthShell title={error ? 'Kontor ist nicht erreichbar' : 'Kontor wird geladen'}>
        {error ? <><p className="auth-error" role="alert">{error}</p><button className="btn" onClick={load}><I icon={RotateCw} sm /> Erneut versuchen</button></> : <Spinner />}
      </AuthShell>
    );
  }
  if (auth.mode === 'public-demo') return <>{children}</>;
  if (path === '/invite' || path === '/reset') return <InvitePage kind={path === '/reset' ? 'reset' : 'invite'} onDone={load} />;
  if (auth.needsSetup) return <SetupPage onDone={load} />;
  if (auth.mfaPending) return <MfaPage onDone={load} />;
  if (!auth.user) return <LoginPage onDone={load} emailLogin={!!auth.emailLogin} />;
  if (auth.enrolRequired) {
    return (
      <AuthShell title="Zwei-Faktor-Anmeldung einrichten" lead="In diesem Treuhandbüro ist die Anmeldung mit einem zweiten Faktor Pflicht. Richte sie jetzt mit einer Authenticator-App ein.">
        <TotpEnrol onDone={load} />
        <SignOut onDone={load} />
      </AuthShell>
    );
  }
  return <>{children}</>;
}

export function AuthShell({ title, lead, children }: { title: string; lead?: ReactNode; children: ReactNode }) {
  useEffect(() => { document.title = `${title} · Kontor`; }, [title]);
  return (
    <main className="auth">
      <div className="auth-card">
        <div className="auth-brand">
          <span className="brand-mark" aria-hidden><svg viewBox="0 0 32 32" width="16" height="16"><path d="M10 7v18M10 16l9-8M13.5 13l8 11" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" /></svg></span>
          <span>Kontor</span>
        </div>
        <h1>{title}</h1>
        {lead && <p className="auth-lead">{lead}</p>}
        {children}
      </div>
      <p className="auth-foot">Buchhaltung nach Schweizer Recht · Daten verschlüsselt übertragen</p>
    </main>
  );
}

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}{hint && <small className="dim">{hint}</small>}</label>;
}

function useSubmit(fn: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await fn(); } catch (x) { setError((x as Error).message); }
    setBusy(false);
  };
  return { busy, error, submit };
}

/** The browser remembers how this person signed in last time, and the address for the code (per device only). */
const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};

function LoginPage({ onDone, emailLogin }: { onDone: () => void; emailLogin: boolean }) {
  const [mode, setModeState] = useState<'code' | 'password'>(() => (emailLogin && store.get('kontor-login-mode') !== 'password' ? 'code' : 'password'));
  const setMode = (m: 'code' | 'password') => { store.set('kontor-login-mode', m); setModeState(m); };
  if (mode === 'code') return <CodeLogin onDone={onDone} onPassword={() => setMode('password')} />;
  return <PasswordLogin onDone={onDone} onCode={emailLogin ? () => setMode('code') : undefined} />;
}

function PasswordLogin({ onDone, onCode }: { onDone: () => void; onCode?: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const s = useSubmit(async () => { await post('/api/auth/login', { email, password }); onDone(); });
  return (
    <AuthShell title="Anmelden" lead="Mit deiner E-Mail-Adresse und deinem Passwort.">
      <form className="stack" onSubmit={s.submit}>
        <Field label="E-Mail-Adresse"><input className="input" type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Passwort"><input className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {s.error && <p className="auth-error" role="alert">{s.error}</p>}
        <button className="btn primary auth-submit" disabled={s.busy}>{s.busy ? <Spinner /> : <I icon={LogIn} sm />} Anmelden</button>
        <p className="dim auth-note">Passwort vergessen? Eine Person mit der Rolle Admin schickt dir einen Link zum Zurücksetzen.</p>
      </form>
      {onCode && <button className="btn ghost auth-signout" onClick={onCode}><I icon={Mail} sm /> Mit Code per E-Mail anmelden</button>}
    </AuthShell>
  );
}

/** Seconds until another code may be requested (a new code replaces the previous one). */
function useCooldown(from: number | null, seconds = 30) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (from === null) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [from]);
  return from === null ? 0 : Math.min(seconds, Math.max(0, Math.ceil(seconds - (now - from) / 1000)));
}

function CodeLogin({ onDone, onPassword }: { onDone: () => void; onPassword: () => void }) {
  const [email, setEmail] = useState(() => store.get('kontor-login-email') ?? '');
  const [sent, setSent] = useState<{ at: number; minutes: number } | null>(null);
  const [code, setCode] = useState('');
  const [again, setAgain] = useState<string | null>(null);
  const wait = useCooldown(sent?.at ?? null);
  const request = async () => {
    const r = await post<{ minutes: number }>('/api/auth/code/request', { email });
    store.set('kontor-login-email', email.trim());
    setSent({ at: Date.now(), minutes: r.minutes });
    setCode('');
  };
  const ask = useSubmit(request);
  const check = useSubmit(async () => { await post('/api/auth/code/verify', { email, code }); onDone(); });
  if (!sent) {
    return (
      <AuthShell title="Anmelden" lead="Mit deiner E-Mail-Adresse. Kontor schickt dir einen Code, ein Passwort brauchst du nicht.">
        <form className="stack" onSubmit={ask.submit}>
          <Field label="E-Mail-Adresse"><input className="input" type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          {ask.error && <p className="auth-error" role="alert">{ask.error}</p>}
          <button className="btn primary auth-submit" disabled={ask.busy}>{ask.busy ? <Spinner /> : <I icon={Mail} sm />} Code senden</button>
        </form>
        <button className="btn ghost auth-signout" onClick={onPassword}><I icon={KeyRound} sm /> Mit Passwort anmelden</button>
      </AuthShell>
    );
  }
  return (
    <AuthShell title="Code eingeben" lead={<>Ist <b>{email.trim()}</b> für die Anmeldung per Code eingerichtet, ist jetzt ein sechsstelliger Code unterwegs. Er gilt {sent.minutes} Minuten und nur einmal.</>}>
      <form className="stack" onSubmit={check.submit}>
        <Field label="Code aus der E-Mail"><input className="input mono auth-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required autoFocus value={code} onChange={(e) => setCode(e.target.value)} /></Field>
        {(check.error || again) && <p className="auth-error" role="alert">{check.error ?? again}</p>}
        <button className="btn primary auth-submit" disabled={check.busy}>{check.busy ? <Spinner /> : <I icon={LogIn} sm />} Anmelden</button>
        <div className="auth-links">
          <button type="button" className="btn ghost sm" onClick={() => { setSent(null); setAgain(null); }}><I icon={ArrowLeft} sm /> Andere Adresse</button>
          <button type="button" className="btn ghost sm" disabled={wait > 0} onClick={async () => { setAgain(null); try { await request(); } catch (e) { setAgain((e as Error).message); } }}>
            <I icon={RotateCw} sm /> {wait > 0 ? `Neuer Code in ${wait} s` : 'Neuen Code senden'}
          </button>
        </div>
        <p className="dim auth-note">Keine E-Mail erhalten? Den Spam-Ordner prüfen. Meldest du dich mit einem Passwort an, kommt statt des Codes ein Hinweis.</p>
      </form>
    </AuthShell>
  );
}

function SetupPage({ onDone }: { onDone: () => void }) {
  const [x, setX] = useState({ token: '', name: '', email: '', title: '', password: '', repeat: '' });
  const set = (k: keyof typeof x) => (e: { target: { value: string } }) => setX({ ...x, [k]: e.target.value });
  const s = useSubmit(async () => {
    if (x.password !== x.repeat) throw new Error('Die beiden Passwörter stimmen nicht überein');
    await post('/api/auth/setup', { token: x.token.trim(), name: x.name, email: x.email, title: x.title || null, password: x.password });
    navigate('/', true);
    onDone();
  });
  return (
    <AuthShell title="Kontor einrichten" lead={<>Die erste Person wird Admin: sie lädt alle weiteren ein und legt die Mandanten an. Das Setup-Token steht im Server-Log beim Start («First-run setup») oder in der Variable <code>KONTOR_SETUP_TOKEN</code>.</>}>
      <form className="stack" onSubmit={s.submit}>
        <Field label="Setup-Token"><input className="input mono" required autoFocus autoComplete="off" spellCheck={false} value={x.token} onChange={set('token')} /></Field>
        <div className="form-grid">
          <Field label="Name"><input className="input" required autoComplete="name" value={x.name} onChange={set('name')} /></Field>
          <Field label="Funktion (optional)"><input className="input" placeholder="z. B. Treuhand" value={x.title} onChange={set('title')} /></Field>
        </div>
        <Field label="E-Mail-Adresse"><input className="input" type="email" required autoComplete="username" value={x.email} onChange={set('email')} /></Field>
        <div className="form-grid">
          <Field label="Passwort" hint="Mindestens 12 Zeichen"><input className="input" type="password" required minLength={12} autoComplete="new-password" value={x.password} onChange={set('password')} /></Field>
          <Field label="Passwort wiederholen"><input className="input" type="password" required minLength={12} autoComplete="new-password" value={x.repeat} onChange={set('repeat')} /></Field>
        </div>
        {s.error && <p className="auth-error" role="alert">{s.error}</p>}
        <button className="btn primary auth-submit" disabled={s.busy}>{s.busy ? <Spinner /> : <I icon={UserPlus} sm />} Admin anlegen und anmelden</button>
      </form>
    </AuthShell>
  );
}

function MfaPage({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState('');
  const s = useSubmit(async () => { await post('/api/auth/totp/verify', { code }); onDone(); });
  return (
    <AuthShell title="Bestätigungscode" lead="Gib den sechsstelligen Code aus deiner Authenticator-App ein.">
      <form className="stack" onSubmit={s.submit}>
        <Field label="Code"><input className="input mono auth-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required autoFocus value={code} onChange={(e) => setCode(e.target.value)} /></Field>
        {s.error && <p className="auth-error" role="alert">{s.error}</p>}
        <button className="btn primary auth-submit" disabled={s.busy}>{s.busy ? <Spinner /> : <I icon={ShieldCheck} sm />} Bestätigen</button>
      </form>
      <SignOut onDone={onDone} label="Mit einem anderen Konto anmelden" />
    </AuthShell>
  );
}

interface LinkInfo { email: string; name: string; loginMethod: 'password' | 'email_code'; emailLogin: boolean }

function InvitePage({ kind, onDone }: { kind: 'invite' | 'reset'; onDone: () => void }) {
  const token = typeof location !== 'undefined' ? location.hash.slice(1) : '';
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [bad, setBad] = useState<string | null>(null);
  useEffect(() => { post<LinkInfo>('/api/auth/invite/info', { token, kind }).then(setInfo).catch((e) => setBad(e.message)); }, [token, kind]);
  const finish = () => { history.replaceState(null, '', '/'); navigate('/', true); onDone(); };
  const title = kind === 'invite' ? 'Willkommen bei Kontor' : 'Neues Passwort';
  if (bad) return <AuthShell title={title}><p className="auth-error" role="alert">{bad}</p><a className="btn" href="/">Zur Anmeldung</a></AuthShell>;
  if (!info) return <AuthShell title={title}><Spinner /></AuthShell>;
  if (kind === 'invite' && info.loginMethod === 'email_code') return <InviteByCode token={token} info={info} onDone={finish} />;
  return <InvitePassword token={token} kind={kind} info={info} onDone={finish} />;
}

/** Invitation to sign in by code: a first code confirms the address and accepts the invitation. */
function InviteByCode({ token, info, onDone }: { token: string; info: LinkInfo; onDone: () => void }) {
  const [sent, setSent] = useState<{ at: number; minutes: number } | null>(null);
  const [code, setCode] = useState('');
  const [again, setAgain] = useState<string | null>(null);
  const wait = useCooldown(sent?.at ?? null);
  const request = async () => { const r = await post<{ minutes: number }>('/api/auth/invite/code', { token }); setSent({ at: Date.now(), minutes: r.minutes }); setCode(''); };
  const ask = useSubmit(request);
  const check = useSubmit(async () => { await post('/api/auth/invite', { token, code }); onDone(); });
  if (!info.emailLogin) {
    return (
      <AuthShell title="Willkommen bei Kontor">
        <p className="auth-error" role="alert">Diese Kontor-Instanz kann gerade keine E-Mails versenden, deshalb kommt kein Code. Bitte der Person Bescheid geben, die dich eingeladen hat.</p>
      </AuthShell>
    );
  }
  if (!sent) {
    return (
      <AuthShell title="Willkommen bei Kontor" lead={<>Hallo {info.name}. Kontor schickt dir einen Code an <b>{info.email}</b>. Mit ihm aktivierst du deinen Zugang und bist angemeldet.</>}>
        <form className="stack" onSubmit={ask.submit}>
          <p className="dim auth-note">Ein Passwort brauchst du nicht. Auch später meldest du dich mit deiner E-Mail-Adresse an und erhältst jedes Mal einen neuen Code.</p>
          {ask.error && <p className="auth-error" role="alert">{ask.error}</p>}
          <button className="btn primary auth-submit" disabled={ask.busy}>{ask.busy ? <Spinner /> : <I icon={Mail} sm />} Code senden</button>
        </form>
      </AuthShell>
    );
  }
  return (
    <AuthShell title="Code eingeben" lead={<>Der Code ist an <b>{info.email}</b> unterwegs. Er gilt {sent.minutes} Minuten und nur einmal.</>}>
      <form className="stack" onSubmit={check.submit}>
        <input type="email" hidden readOnly autoComplete="username" value={info.email} />
        <Field label="Code aus der E-Mail"><input className="input mono auth-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required autoFocus value={code} onChange={(e) => setCode(e.target.value)} /></Field>
        {(check.error || again) && <p className="auth-error" role="alert">{check.error ?? again}</p>}
        <button className="btn primary auth-submit" disabled={check.busy}>{check.busy ? <Spinner /> : <I icon={UserPlus} sm />} Zugang aktivieren</button>
        <div className="auth-links">
          <span />
          <button type="button" className="btn ghost sm" disabled={wait > 0} onClick={async () => { setAgain(null); try { await request(); } catch (e) { setAgain((e as Error).message); } }}>
            <I icon={RotateCw} sm /> {wait > 0 ? `Neuer Code in ${wait} s` : 'Neuen Code senden'}
          </button>
        </div>
      </form>
    </AuthShell>
  );
}

function InvitePassword({ token, kind, info, onDone }: { token: string; kind: 'invite' | 'reset'; info: LinkInfo; onDone: () => void }) {
  const [pw, setPw] = useState({ password: '', repeat: '' });
  const s = useSubmit(async () => {
    if (pw.password !== pw.repeat) throw new Error('Die beiden Passwörter stimmen nicht überein');
    await post(`/api/auth/${kind}`, { token, password: pw.password });
    onDone();
  });
  return (
    <AuthShell title={kind === 'invite' ? 'Willkommen bei Kontor' : 'Passwort wählen'} lead={kind === 'invite' ? <>Hallo {info.name}. Wähle ein Passwort für <b>{info.email}</b>, dann bist du angemeldet.</> : <>Wähle ein Passwort für <b>{info.email}</b>. Ab dann meldest du dich damit an; andere Anmeldungen werden beendet.</>}>
      <form className="stack" onSubmit={s.submit}>
        <input type="email" hidden readOnly autoComplete="username" value={info.email} />
        <Field label="Passwort" hint="Mindestens 12 Zeichen, nicht die E-Mail-Adresse"><input className="input" type="password" required minLength={12} autoFocus autoComplete="new-password" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} /></Field>
        <Field label="Passwort wiederholen"><input className="input" type="password" required minLength={12} autoComplete="new-password" value={pw.repeat} onChange={(e) => setPw({ ...pw, repeat: e.target.value })} /></Field>
        {s.error && <p className="auth-error" role="alert">{s.error}</p>}
        <button className="btn primary auth-submit" disabled={s.busy}>{s.busy ? <Spinner /> : <I icon={KeyRound} sm />} Passwort speichern</button>
      </form>
    </AuthShell>
  );
}

function SignOut({ onDone, label = 'Abmelden' }: { onDone: () => void; label?: string }) {
  return <button className="btn ghost auth-signout" onClick={async () => { await fetch('/api/auth/logout', { method: 'POST' }); onDone(); }}><I icon={LogOut} sm /> {label}</button>;
}

/** TOTP enrolment: QR code and key for the authenticator app, confirmed with a first code. */
export function TotpEnrol({ onDone }: { onDone: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; svg: string } | null>(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { post<{ secret: string; svg: string }>('/api/auth/totp/setup', {}).then(setSetup).catch((e) => setErr(e.message)); }, []);
  const s = useSubmit(async () => { await post('/api/auth/totp/enable', { secret: setup!.secret, code }); onDone(); });
  if (err) return <p className="auth-error" role="alert">{err}</p>;
  if (!setup) return <Spinner />;
  return (
    <form className="stack" onSubmit={s.submit}>
      <ol className="auth-steps">
        <li>Authenticator-App öffnen (z. B. Microsoft Authenticator, Google Authenticator, 1Password) und den QR-Code scannen.</li>
        <li>Den sechsstelligen Code eingeben, den die App anzeigt.</li>
      </ol>
      {/* The server renders the QR code (black on white, no script): safe to insert. */}
      <div className="auth-qr" aria-label="QR-Code für die Authenticator-App" role="img" dangerouslySetInnerHTML={{ __html: setup.svg }} />
      <p className="dim auth-note">Ohne Kamera: Schlüssel von Hand eingeben <code className="mono" data-copy-unit>{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code></p>
      <Field label="Code aus der App"><input className="input mono auth-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required value={code} onChange={(e) => setCode(e.target.value)} /></Field>
      {s.error && <p className="auth-error" role="alert">{s.error}</p>}
      <button className="btn primary auth-submit" disabled={s.busy}>{s.busy ? <Spinner /> : <I icon={ShieldCheck} sm />} Zwei-Faktor-Anmeldung einschalten</button>
    </form>
  );
}
